// ============================================
// Portal Auth Utilities — UKA-X01 D2
//
// Password-only sign-in for the Expert Clinical Review Portal.
// Session max: 24 hours from sign-in timestamp.
//
// Flow:
//   1. User enters password on /portal/login
//   2. verifyPassword(pw) → true → createSession() + ensureFirebaseAuth()
//   3. Auth guard checks localStorage session on every protected route load
//   4. Firebase anonymous auth token satisfies Firestore security rules
// ============================================

import { auth } from '../../services/firebase';
import { signInAnonymously } from 'firebase/auth';

export type PortalRole = 'REVIEWER' | 'OPERATOR' | 'ADMIN';

// ── LocalStorage keys ─────────────────────────────────────────────
const SESSION_START_KEY = 'hlx_portal_session_start';
const SESSION_ID_KEY    = 'hlx_portal_session_id';
const SESSION_AUTH_KEY  = 'hlx_portal_authenticated';

/** 24 hours in milliseconds */
const SESSION_MAX_MS = 24 * 60 * 60 * 1000;

// ── Password verification ─────────────────────────────────────────

/** Verify the portal access password. Returns true on match. */
export function verifyPassword(password: string): boolean {
  return password === 'GoGators2026';
}

// ── Session management ────────────────────────────────────────────

/**
 * Signs in anonymously to Firebase so Firestore security rules
 * see request.auth != null. No-op if already signed in.
 */
export async function ensureFirebaseAuth(): Promise<void> {
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }
}

/** Create a new 24-hour session in localStorage. Call after successful password verify. */
export function createSession(): void {
  const sessionId = `portal_${Date.now()}`;
  window.localStorage.setItem(SESSION_START_KEY, Date.now().toString());
  window.localStorage.setItem(SESSION_ID_KEY, sessionId);
  window.localStorage.setItem(SESSION_AUTH_KEY, '1');
}

/** Returns true if an active, non-expired session exists. */
export function isAuthenticated(): boolean {
  return (
    window.localStorage.getItem(SESSION_AUTH_KEY) === '1' &&
    isSessionValid()
  );
}

/**
 * Returns true if the current session is within the 24-hour window.
 * Returns false if no session exists or if the session has expired.
 */
export function isSessionValid(): boolean {
  const raw = window.localStorage.getItem(SESSION_START_KEY);
  if (!raw) return false;
  const start = parseInt(raw, 10);
  if (isNaN(start)) return false;
  return Date.now() - start <= SESSION_MAX_MS;
}

/**
 * Returns true if the stored session start exceeds 24 hours.
 * Returns false if no session start is recorded.
 */
export function isSessionExpired(): boolean {
  const raw = window.localStorage.getItem(SESSION_START_KEY);
  if (!raw) return false;
  const start = parseInt(raw, 10);
  if (isNaN(start)) return false;
  return Date.now() - start > SESSION_MAX_MS;
}

/** Sign out and clear all session state. */
export function signOut(): void {
  window.localStorage.removeItem(SESSION_START_KEY);
  window.localStorage.removeItem(SESSION_ID_KEY);
  window.localStorage.removeItem(SESSION_AUTH_KEY);
}

/** Returns the current session ID for audit trail inclusion. */
export function getSessionId(): string | null {
  return window.localStorage.getItem(SESSION_ID_KEY);
}

/** All password-auth users are treated as REVIEWER role. */
export function getUserRole(): PortalRole {
  return 'REVIEWER';
}

/** Session IP — not available in password-auth mode. */
export async function getSessionIpAddress(_sessionId: string): Promise<string> {
  return 'password-auth';
}
